import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, tweensAxis } from '../skillUsage';

export const extrusionFlyInSkill: CodeoSkill = {
    name: SKILL_NAMES.extrusionFlyIn,
    kind: 'codeo',
    description:
        'Fly a bold italic word in diagonally from a corner as a solid 3D block whose extrusion ' +
        'trails far behind it, then snap the extrusion back to a short, steep depth as it lands — ' +
        'the loudest hero treatment in a calm typographic video, for the word that is the point.',
    checkLooksFor:
        'an extrusion behind the word (stacked copies or text-shadow) while the word moves on x and y',
    documentUsesSkill: check(
        (script, markup, html) =>
            /background-clip:\s*text|backgroundClip|textShadow|text-shadow/.test(html) &&
            tweensAxis(script, 'x') &&
            tweensAxis(script, 'y'),
    ),
    instructions: `# Extrusion Fly-In

The payoff word ("Whole campaigns") arrives as an object. It flies in on a 45° diagonal from below and to the right, **trailing an extrusion so deep it reaches back to where the word came from**, a solid violet wedge sweeping across the frame. As the word lands, the extrusion **snaps back to a short, steep depth**, and the word sits there as a chunky 3D block: white face, violet outline, a gradient-shaded side.

The movement is two halves: it accelerates out of the corner and decelerates into place, with the fastest point in the middle of the path. The snap-back of the extrusion comes after the word has nearly landed, so the landing has a second beat.

## Markup

The face is the word itself, white, with a thin outline drawn under the fill (\`paint-order: stroke fill\`) so the letters keep their full shape. The side is a stack of copies of the word built in script, **all of them beneath the face** and all painted with **the same gradient**, so the side reads as one smooth solid and, on two lines, the depth of both lines merges into one block.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center">
  <div id="mover" data-l5-allow-offscreen style="position: relative;">
    <div id="block" style="position: relative;">
      <div id="face" class="slab">Whole<br>campaigns</div>
    </div>
  </div>
</div>
\`\`\`

\`\`\`css
.slab { font-family: var(--l5-font-0), sans-serif; font-style: italic; font-weight: 700; font-size: 222px; line-height: 0.9; text-align: center; white-space: nowrap; }
#face { position: relative; color: #fff; -webkit-text-stroke: 0.063em var(--l5-color-2, #4f39e6); paint-order: stroke fill; }
.depth {
  position: absolute; inset: 0;
  color: transparent; -webkit-background-clip: text; background-clip: text;
  background-image: linear-gradient(100deg, color-mix(in srgb, var(--l5-color-2, #4f39e6) 55%, #fff) 0%, color-mix(in srgb, var(--l5-color-2, #4f39e6) 75%, #fff) 45%, var(--l5-color-2, #4f39e6) 100%);
}
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 1.4;
const CUE = 0;
const COPIES = 72;                           // copies in the side
const HEADING = { x: 1.065, y: 1 };          // the way back along its path: down and right, about 45°
const MID = 0.52;                            // share of the path covered when it is fastest
const TRAIL = 1.2;                           // the trail reaches this far past the start, so its end is off-frame

const block = document.querySelector('#block');
const face = document.querySelector('#face');
const em = parseFloat(getComputedStyle(face).fontSize);
// The farthest copy goes in first, so every copy paints beneath the ones nearer the face.
const copies = Array.from({ length: COPIES }, (_, i) => {
  const copy = face.cloneNode(true);
  copy.removeAttribute('id');
  copy.className = 'slab depth';
  copy.setAttribute('aria-hidden', 'true');
  copy.setAttribute('data-l5-allow-overlap', '');
  block.insertBefore(copy, face);
  return { copy, k: (COPIES - i) / COPIES };
});
// It starts wholly off-frame: slide the resting block back along the heading until it clears the
// right or bottom edge, whichever comes first. Measured before any tween moves it.
const rest = block.getBoundingClientRect();
const frame = document.documentElement;
const away = Math.min((frame.clientWidth - rest.left) / HEADING.x, (frame.clientHeight - rest.top) / HEADING.y);
const FROM = { x: HEADING.x * away, y: HEADING.y * away };
// While flying, the trail points back along the path and reaches past where the word started;
// at rest it is short and steep.
const depth = { x: FROM.x * TRAIL, y: FROM.y * TRAIL };
const REST = { x: 0.29 * em, y: 0.41 * em };
const extrude = () => {
  copies.forEach(({ copy, k }) => {
    copy.style.transform = \`translate(\${(depth.x * k).toFixed(1)}px, \${(depth.y * k).toFixed(1)}px)\`;
  });
};
extrude();

// Out of the corner accelerating, into place decelerating.
tl.fromTo('#mover', { x: FROM.x, y: FROM.y }, {
  x: FROM.x * (1 - MID), y: FROM.y * (1 - MID), duration: 0.29, ease: 'power3.in',
}, CUE);
tl.to('#mover', { x: 0, y: 0, duration: 0.58, ease: 'power3.out' }, CUE + 0.29);
// The extrusion snaps back once the word has nearly landed.
tl.to(depth, { x: REST.x, y: REST.y, duration: 0.42, ease: 'expo.out', onUpdate: extrude }, CUE + 0.67);

tl.set({}, {}, SCENE);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Two lines.** A two-word payoff stacks, centred, with a \`<br>\` between the lines. The copies carry both lines, so the side stays one block.
- **Direction.** It flies against the reading direction of the last exit: after a phrase lifted up, it comes from bottom-right. To come from another corner, flip the signs of \`HEADING\` and measure \`away\` against the edges on that side.
- **Colours.** The outline and the deep end of the side are the brand's deepest colour; the near end is a lighter tint of it. The face stays white (or the ground's colour): the outline is what draws it.
- **Fewer copies for small type.** 72 copies suit 200px+ type; at 120px, 40 are enough.

## Pitfalls

- **Start off-frame.** The word enters the frame; it is never on screen at its first frame. Work the start out from the resting position, as above, rather than picking an offset.
- **The trail is the path.** The depth while flying is the flight vector scaled past the start, so the side points straight back the way the word came and its far end stays beyond the frame edge for the whole flight. A depth chosen separately from the flight points the side off at its own angle, and a short one leaves no wedge for the snap-back to collapse.

- **One gradient for the whole side.** Every copy takes the same gradient. Giving each copy its own shade turns the side into visible bands, and the steps between copies show as a staircase.
- **Every copy under the face.** Insert the copies before the face in the same block. Two lines extruded separately, or a \`text-shadow\` extrusion, can paint one line's side over the other line's letters.
- **A white face.** A face in the same hue as its side loses its edge, and the word reads as a smudge.
- **\`paint-order: stroke fill\`.** Without it the stroke eats into the face and the letters thin out.
- **Extrusion inside, motion outside.** Move \`#mover\`, lay out the copies inside \`#block\`; animating both on one element makes the depth swing with the flight.
- **The offscreen mark on the word, not the frame.** \`data-l5-allow-offscreen\` goes on the word-sized \`#mover\`. On a full-frame wrapper it marks the composition root, which the lint rejects.
- **The copies are decoration.** They carry \`aria-hidden\` and \`data-l5-allow-overlap\`, so they are never read as a second word.
- **Use it once.** One extruded word per video; it is the payoff, and a second one is a gimmick.
- The wrapper is transform-tweened, so it carries no CSS \`transform\`.`,
};
